import { Component, HostBinding, OnInit, ViewChild } from '@angular/core';
import { RowNode } from 'ag-grid-community';
import { Connector, MountedConnectorMode, Type } from '../../../../models/connector';
import { ContextMenuOption } from '../../../../models/context-menu-option';
import { OperationType, ConnectorType, Endpoint, HelpIndex, UpdateStatusType, StatusCode } from '../../../../models/enums';
import { CellTemplateType, GenericColDef } from '../../../../models/grid-context';
import { CommonService } from '../../../../services/common/common.service';
import { RestService } from '../../../../services/rest/rest.service';
import { ConnectorsModalComponent } from '../../../modals/connectors-modal/connectors-modal.component';
import { DeleteEntityModalComponent } from '../../delete-entity-modal/delete-entity-modal.component';
import { ObjectActionListType, ActionType, Permissions } from '../../../../models/permission';
import * as _ from 'underscore';
import { GenericTreeNode } from '../../../generic-tree/generic-tree-node';
import { SettingsNavigationContentComponent } from '../../../../models/settings-navigation-content-component';
import { GenericGridComponent } from '../../../generic-grid/generic-grid.component';

@Component({
    standalone: false,
    selector: 'res-connectors-list',
    templateUrl: './connectors-list.component.html',
    styleUrls: ['./connectors-list.component.scss']
})
export class ConnectorsListComponent implements SettingsNavigationContentComponent, OnInit {
    @HostBinding('class') classes = 'maximized-component';
    @ViewChild(GenericGridComponent, { static: true }) genericGrid: GenericGridComponent;

    isDirty: boolean;
    connector: Connector;

    gridName = 'Connectors';
    automationId = 'connectors-list';
    helpIndex = HelpIndex.connectors_org_manage_dlg;
    updateStatusType = UpdateStatusType.CONNECTOR;
    validChanges = true;

    isBusy = false;
    isBusyMessage = '';

    // permissions
    hasConnectorViewOnlyPermissions: boolean;
    hasConnectorCreatePermissions: boolean;
    hasConnectorDeletePermissions: boolean;

    columnDefs = new Array<GenericColDef>(
        {
            headerName: 'Connector Name',
            field: 'name',
            automationId: this.automationId,
            lockVisible: true
        },
        {
            headerName: 'Description',
            field: 'description',
            automationId: this.automationId
        },
        {
            headerName: 'Type',
            field: 'displayType',
            automationId: this.automationId,
            width: 110,
            suppressSizeToFit: true
        },
        {
            headerName: 'Mode',
            field: 'modeDisplay',
            automationId: this.automationId,
            width: 95,
            suppressSizeToFit: true
        },
        {
            headerName: 'Server',
            field: 'remoteHost',
            automationId: this.automationId
        },
        {
            headerName: 'Path / Domain',
            field: 'remotePath',
            automationId: this.automationId
        },
        {
            headerName: 'Status',
            field: 'status',
            automationId: this.automationId
        },
        {
            automationId: this.automationId,
            cellTemplateType: CellTemplateType.contextMenu,
            minWidth: 30, maxWidth: 30, width: 30,
            suppressSizeToFit: true
        },
    );

    rowClassRules = {
        'disabled-row': () => {
            return this.hasConnectorViewOnlyPermissions;
        }
    };

    requestInfo = {
        endpoint: Endpoint.adminOrgManager_listConnectors,
        responseName: 'connectors',
        data: {
            organizationName: this.commonService?.currentUser?.customerName
        }
    };
    contextMenuOptions = [
        new ContextMenuOption({
            name: 'Validate', iconClass: 'fa-check', iconColor: 'color-blue-01',
            click: (item) => this.validateConnector(item),
            permissions: [{ permissionType: ObjectActionListType.CONNECTOR, permissionActions: [ActionType.CREATE] }]
        }),
        new ContextMenuOption({
            name: 'Edit...', iconClass: 'fa-pencil', iconColor: 'color-blue-01', automationId: 'org-settings-connectors-list-edit-connector',
            click: (item) => this.editConnector(item),
            permissions: [{ permissionType: ObjectActionListType.CONNECTOR, permissionActions: [ActionType.CREATE] }]
        }),
        new ContextMenuOption({
            name: 'Deactivate', automationId: 'org-settings-connectors-list-deactivate-connector',
            enabled: (item) => item?.status !== StatusCode.DEACTIVATED,
            click: (item) => this.deactivateConnector(item),
            permissions: [{ permissionType: ObjectActionListType.CONNECTOR, permissionActions: [ActionType.CREATE] }]
        }),
        new ContextMenuOption({
            name: 'Delete', iconClass: 'fa-trash', iconColor: 'color-red-05', automationId: 'org-settings-connectors-list-delete-connector',
            click: (item) => this.deleteConnector(item),
            permissions: [{ permissionType: ObjectActionListType.CONNECTOR, permissionActions: [ActionType.DELETE] }]
        })
    ];

    responseArrayCallback = (value: any): Connector => {
        const connector = new Connector(value);
        connector.displayType = connector.mountedConnectorMode === MountedConnectorMode.PANFS ? "NFS (PanFS)" as Type : connector.type;
        return connector;
    }

    cellDoubleClickedCallback = (rowNode: RowNode) => {
        if (rowNode && rowNode.data instanceof Connector) {
            this.editConnector(rowNode.data);
        }
    }

    constructor(private commonService: CommonService,
        private restService: RestService) {

        if (this.columnDefs.find(c => c.cellTemplateType === CellTemplateType.contextMenu)) {
            if (Permissions.checkUserPermission(ObjectActionListType.CONNECTOR) &&
                !Permissions.checkUserPermissions([{ permissionType: ObjectActionListType.CONNECTOR, permissionActions: [ActionType.CREATE] }])) {
                this.columnDefs = this.columnDefs.filter(col => col.cellTemplateType !== CellTemplateType.contextMenu);
            }
        }
    }

    ngOnInit() {
        this.hasConnectorCreatePermissions = Permissions.checkUserPermissions([{
            permissionType: ObjectActionListType.CONNECTOR,
            permissionActions: [ActionType.CREATE]
        }]);
        this.hasConnectorViewOnlyPermissions = Permissions.checkUserPermission(
            ObjectActionListType.CONNECTOR
        ) && !this.hasConnectorCreatePermissions;
        this.hasConnectorDeletePermissions = Permissions.checkUserPermissions([{
            permissionType: ObjectActionListType.CONNECTOR,
            permissionActions: [ActionType.DELETE]
        }]);
    }

    deleteConnector(item: Connector): void {
        this.connector = item;

        this.restService.post(Endpoint.orgManager_listDataAreas, { connectorHandle: this.connector.handle }).then(daResponse => {
            const dataAreaList = !daResponse || !daResponse.dataAreas ? [] : _.pluck(daResponse.dataAreas, 'name');
            if (dataAreaList.length === 0) {
                this.commonService.openModal(DeleteEntityModalComponent, { modalData: item });
            } else {
                this.commonService.openErrorModal(this.connector.name + ' cannot be deleted because it is used in these data areas:\r\n' +
                    dataAreaList.join('\r\n'), 'Delete ' + this.connector.name);
            }
        });
    }

    validateConnector(item): void {
        if (item) {
            let endpoint: Endpoint;
            let request = null;
            switch (item.type) {
                case Type.NFS: {
                    endpoint = Endpoint.connectorManager_validateNFSConnector;
                    request = _.pick(item, 'name', 'description', 'remoteHost', 'remotePath', 'readOnly', 'mountedConnectorMode');
                    Object.assign(request, { edit: true });
                    break;
                }
                case Type.CIFS: {
                    endpoint = Endpoint.connectorManager_validateCIFSConnector;
                    request = _.pick(item, 'name', 'description', 'remoteHost', 'remotePath', 'readOnly', 'protocol', 'authenticationType', 'userName',
                        'password', 'workgroupOrDomain', 'supportSMB1');
                    Object.assign(request, { edit: true });
                    break;
                }
                case Type.Exchange: {
                    endpoint = Endpoint.connectorManager_validateExchangeConnector;
                    request = _.pick(item, 'name', 'description', 'readOnly', 'username', 'password', 'contentUrl', 'domainController', 'azureADEnabled',
                        'tenantDomain', 'applicationId', 'applicationSecret');
                    break;
                }
                case Type.Sharepoint: {
                    endpoint = Endpoint.connectorManager_validateSharepointConnector;
                    request = _.pick(item, 'name', 'description', 'password', 'contentUrl', 'readOnly', 'domainController', 'azureADEnabled', 'tenantDomain',
                        'applicationId', 'applicationSecret', 'refreshToken', 'oneDriveTenantDomain', 'oneDriveApplicationId', 'oneDriveApplicationSecret', 'oneDriveRefreshToken');
                    Object.assign(request, { username: item.userName });
                    break;
                }
                case Type.GoogleWorkspace: {
                    endpoint = Endpoint.connectorManager_validateGoogleWorkspaceConnector;
                    request = _.pick(item, 'name', 'description', 'refreshToken', 'serviceAccountEmail', 'serviceAccountKey', 'tenantDomain');
                    Object.assign(request, { username: item.userName });
                    break;
                }
            }
            if (endpoint && request) {
                this.isBusy = true;
                this.isBusyMessage = `Validating ${item.type?.toString()} Connector...`;
                this.restService.post(endpoint, request, false).then(response => {
                    if (response?.valid) {
                        this.commonService.openSuccessToastNotification(`The Connector ${item.name} is valid.`);
                    }
                    this.isBusy = false;
                }, error => {
                    if (error?.extendedStatus) {
                        this.commonService.openErrorModal(error.extendedStatus);
                    }
                    this.isBusy = false;
                });
            }
        }
    }

    deactivateConnector(item): void {
        const msg = 'This operation will deactivate the selected Connector. Make sure all associated Jobs are complete before proceeding.\nAre you sure you want to deactivate the selected Connector?';
        this.commonService.openConfirmationModal(msg, 'Deactivate Connector', 'OK', 'Cancel').then(result => {
            if (result.modalResult) {
                this.restService.post(Endpoint.adminOrgManager_deactivateConnectors, { handles: [item.name] })
                    .then(() => {
                        this.genericGrid.loadData();
                    });
            }
        });
    }

    editConnector(item: Connector): void {
        this.commonService.openModal(ConnectorsModalComponent,
            { backdrop: false, modalData: { operationType: OperationType.edit, connectorType: item.type, connectorItem: item } });
    }

    addNewConnector(): void {
        this.commonService.openModal(ConnectorsModalComponent,
            { backdrop: false, modalData: { operationType: OperationType.new, connectorType: ConnectorType.NFS } });
    }

    selectedTreeItemChanged = (node: GenericTreeNode, projectHandle?: string) => {
        this.isBusy = false;
    };

    saveChanges(): Promise<boolean> { return Promise.resolve(true); }
}
